import {
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { Profile } from '@/types/db';
import {
  signedPrescriptionUrl,
  type UploadedAttachment,
} from '@/features/timeline/lib/upload';
import { PhotoLightbox } from './photo-lightbox';
import { PdfPreview } from '@/components/pdf-preview';

const DISCLAIMER =
  'This app stores records. It does not provide medical advice. For clinical decisions, consult a licensed clinician.';

interface Props {
  profile: Profile;
  attachment: UploadedAttachment;
  headerTitle: string;
  photoCaption: string;
  photoAlt: string;
  cancelMessage: string;
  onSubmit: () => void;
  submitting: boolean;
  saveButtonLabel?: string;
  saveError?: string | null;
  children: ReactNode;
}

export function ReviewChrome({
  profile,
  attachment,
  headerTitle,
  photoCaption,
  photoAlt,
  cancelMessage,
  onSubmit,
  submitting,
  saveButtonLabel = 'Save to timeline',
  saveError,
  children,
}: Props) {
  const navigate = useNavigate();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoErr, setPhotoErr] = useState(false);
  const [photoReloadKey, setPhotoReloadKey] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPhotoErr(false);
    setPhotoUrl(null);
    signedPrescriptionUrl(attachment.storage_path)
      .then((url) => {
        if (!cancelled) setPhotoUrl(url);
      })
      .catch((err) => {
        console.error('[review] signed url failed', err);
        if (!cancelled) setPhotoErr(true);
      });
    return () => {
      cancelled = true;
    };
  }, [attachment, photoReloadKey]);

  const retryPhoto = useCallback(() => setPhotoReloadKey((k) => k + 1), []);

  const handleCancel = useCallback(() => {
    const dest = `/profile/${profile.id}`;
    if (typeof window !== 'undefined') {
      const ok = window.confirm(cancelMessage);
      if (!ok) return;
    }
    navigate(dest, { replace: true });
  }, [navigate, profile.id, cancelMessage]);

  return (
    <main className="min-h-screen safe-top pb-32">
      <header className="sticky top-0 z-10 border-b border-border bg-surface/95 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={handleCancel}
            aria-label="Cancel"
            className="h-10 w-10 -ml-1 rounded-full flex items-center justify-center text-ink-muted hover:text-ink active:bg-canvas shrink-0"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="text-lg font-bold truncate flex-1">{headerTitle}</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-4 md:grid md:grid-cols-2 md:gap-6 md:items-start">
        <section className="mb-4 md:mb-0">
          <div className="card overflow-hidden">
            {photoUrl && !photoErr ? (
              attachment.file_type === 'application/pdf' ? (
                <PdfPreview
                  url={photoUrl}
                  fileName={attachment.file_name}
                  variant="full"
                  helperText={photoCaption}
                />
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setLightboxOpen(true)}
                    aria-label={`Tap to zoom the ${photoAlt.toLowerCase()}`}
                    className="block w-full"
                  >
                    <img
                      src={photoUrl}
                      alt={photoAlt}
                      className="w-full h-auto max-h-[60vh] object-contain bg-canvas"
                    />
                  </button>
                  <p className="text-xs text-ink-muted px-3 py-2 border-t border-border bg-canvas">
                    {photoCaption}
                  </p>
                </>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-ink-muted text-sm gap-3">
                {photoErr ? (
                  <>
                    <p>Photo couldn't load.</p>
                    <button
                      type="button"
                      onClick={retryPhoto}
                      className="btn-secondary"
                    >
                      Try again
                    </button>
                  </>
                ) : (
                  <p>Loading photo…</p>
                )}
              </div>
            )}
          </div>
        </section>

        <section>
          {children}

          {saveError && (
            <p className="text-sm text-flag-block mt-3" role="alert">
              {saveError}
            </p>
          )}

          <div className="rounded-lg border border-border bg-canvas p-3 mt-4">
            <p className="text-sm text-ink-muted">{DISCLAIMER}</p>
          </div>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 safe-bottom bg-surface border-t border-border px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="btn-secondary px-4"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            aria-describedby={submitting ? 'save-hint' : undefined}
            className="btn-primary flex-1"
          >
            {submitting ? 'Saving…' : saveButtonLabel}
          </button>
        </div>
        {submitting && (
          <p
            id="save-hint"
            className="max-w-3xl mx-auto mt-1.5 text-[11px] text-ink-muted text-center"
          >
            Writing record — don't close the app.
          </p>
        )}
      </div>

      {lightboxOpen && photoUrl && (
        <PhotoLightbox
          src={photoUrl}
          alt={photoAlt}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </main>
  );
}
